import { inject, Injectable } from "@angular/core";
import { DetectionObject, DetectionResult, DetectionResultItem } from "../../model/detection/detection.models";
import { ExportFormat } from "../../../shared-components/export-dialog/export-dialog.models";
import { GeoService } from "../../../viewer-components/services/internal-services/geo.service";
import { MaskVectorizerService } from "../detection/mask-vectorizer.service";
import { GoogleAnalyticsService } from "../../../shared/services/google-analytics.service";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../shared/model/google-analytics-constants";

interface GeoJsonFeature {
  type: "Feature";
  geometry: { type: "Polygon"; coordinates: number[][][] };
  properties: Record<string, unknown>;
}

/**
 * Builds and triggers a browser download of Custom Elements detection results
 * in the requested format. Supports GeoJSON and WKT (extensible via ExportFormat).
 */
@Injectable({ providedIn: "root" })
export class DetectionExportService {
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);

  exportResults(
    format: ExportFormat,
    layerIds: string[] | null,
    objects: readonly DetectionObject[],
    results: Readonly<Record<string, DetectionResult>>,
  ): void {
    const filtered = layerIds ? objects.filter((o) => layerIds.includes(o.name)) : objects;
    const total = filtered.reduce((s, o) => s + (results[o.name]?.detections.length ?? 0), 0);

    if (total === 0) return;

    try {
      switch (format.id) {
        case "geojson":
          this.downloadGeoJson(filtered, results);
          break;
        case "wkt":
          this.downloadWkt(filtered, results);
          break;
        default:
          console.warn("DetectionExportService: unsupported format", format.id);
          return;
      }
      this.trackExport(format);
    } catch (err) {
      console.error("DetectionExportService: export failed", err);
    }
  }

  /** Logs one GA event per exported object type, so per-type/format export popularity can be tracked. */
  private trackExport(format: ExportFormat): void {
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.CUSTOM_ELEMENT_DETECTION_EXPORT,
      GoogleAnalyticsConstantsCategory.CUSTOM_ELEMENTS_DETECTION,
      true,
      format.id,
    );
  }

  private downloadGeoJson(
    objects: readonly DetectionObject[],
    results: Readonly<Record<string, DetectionResult>>,
  ): void {
    const features: GeoJsonFeature[] = [];
    for (const obj of objects) {
      const res = results[obj.name];
      if (!res) continue;
      for (const det of res.detections) {
        const geom = this.parsePolygonWkt(det.bboxWkt);
        if (!geom) continue;
        features.push({
          type: "Feature",
          geometry: geom,
          properties: { ...this.buildProperties(obj, det), kind: "bbox" },
        });
        for (const maskWkt of this.maskPolygonWkts(det)) {
          const maskGeom = this.parsePolygonWkt(maskWkt);
          if (!maskGeom) continue;
          features.push({
            type: "Feature",
            geometry: maskGeom,
            properties: { ...this.buildProperties(obj, det), kind: "mask" },
          });
        }
      }
    }
    const fc = { type: "FeatureCollection", features };
    triggerDownload(JSON.stringify(fc, null, 2), buildFilename("detections", "geojson"), "application/geo+json");
  }

  /** Parses a `POLYGON(...)` WKT into GeoJSON geometry via {@link GeoService}. Returns `null` for missing/invalid WKT. */
  private parsePolygonWkt(wkt: string | undefined): { type: "Polygon"; coordinates: number[][][] } | null {
    if (!wkt) return null;
    try {
      const geom = GeoService.wktToGeoJSONPolygon(wkt, false);
      return geom?.type === "Polygon" ? (geom as { type: "Polygon"; coordinates: number[][][] }) : null;
    } catch (err) {
      console.error("DetectionExportService: failed to parse bbox WKT:", err, wkt);
      return null;
    }
  }

  private downloadWkt(objects: readonly DetectionObject[], results: Readonly<Record<string, DetectionResult>>): void {
    const polygons: string[] = [];
    for (const obj of objects) {
      const res = results[obj.name];
      if (!res) continue;
      for (const det of res.detections) {
        if (!det.bboxWkt) continue;
        // Strip "POLYGON " prefix to get the ring portion for MULTIPOLYGON
        polygons.push(det.bboxWkt.replace(/^POLYGON\s*/i, ""));
        // Masks are appended unlabeled — WKT cannot carry per-polygon metadata.
        for (const maskWkt of this.maskPolygonWkts(det)) {
          polygons.push(maskWkt.replace(/^POLYGON\s*/i, ""));
        }
      }
    }
    const wkt = polygons.length === 1 ? `POLYGON ${polygons[0]}` : `MULTIPOLYGON (${polygons.join(", ")})`;
    triggerDownload(wkt, buildFilename("detections", "wkt"), "text/plain");
  }

  /** Mask polygons in geographic WKT, or empty when the detection has no vectorised mask or a valid bbox frame. */
  private maskPolygonWkts(det: DetectionResultItem): string[] {
    const mask = det.maskRings;
    if (!mask || !det.bboxWkt) return [];
    const ext = GeoService.wktPolygonToBbox(det.bboxWkt, false) as [number, number, number, number] | undefined;
    if (!ext || ext.length < 4) return [];
    return MaskVectorizerService.groupRingsIntoPolygons(mask.rings)
      .map((poly) => MaskVectorizerService.maskPolygonToWkt(poly, mask.w, mask.h, ext))
      .filter((w): w is string => !!w);
  }

  private buildProperties(obj: DetectionObject, det: DetectionResultItem): Record<string, unknown> {
    return {
      objectName: obj.name,
      confidence: det.confidence,
      captureDate: det.captureDate ?? null,
      imageId: det.imageId ?? null,
      color: obj.color,
    };
  }
}

function buildFilename(prefix: string, ext: string): string {
  const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  return `${prefix}-${ts}.${ext}`;
}

function triggerDownload(content: string, filename: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
